import type { SbBlokData } from "@storyblok/react";
import type { ScrollDirection } from "hooks/useScrollDirection";
import { trackUAClickEvent } from "utils/tracking";
import { getLinkSource } from "../globalComponents.utils";
import type { HeaderLink, HeaderLinksGroup } from "./types";

/**
 * Checks if the given component is a HeaderLinksGroup.
 * @param component - The component to check.
 * @returns True if the component is a HeaderLinksGroup, false otherwise.
 */
export const isHeaderLinkGroup = (
  component: SbBlokData | HeaderLinksGroup | HeaderLink
): component is HeaderLinksGroup => component.component === "headerLinksGroup";

/**
 * Check if the given link is active based on current URL's pathname.
 * @param linkSource - The source of the link to check against.
 * @param path - The current URL's pathname.
 * @returns An object that contain active link source and boolean indicator of the link's active state.
 */
export const getActiveLink = (linkSource: string, path: string) => {
  const slug = path.split("/")[2] || undefined;
  const isActiveLink = linkSource.split("/")[2] === slug;

  const activeLinkPath = isActiveLink ? linkSource : "";

  return { isActiveLink, activeLinkPath };
};

/**
 * Parses a tracking event string, extracts an event from the JSON string, and sends the event to UA (User Analytics) as a click event
 * @param trackingEventString - The tracking event string to parse.
 */
export const handleTrackingEvents = (trackingEventString: HeaderLink["headerLinkTrackingEvent"]) => {
  const trackingEvent = trackingEventString && JSON.parse(trackingEventString);
  if (trackingEvent?.length === 2) {
    const [section, element] = trackingEvent;
    trackUAClickEvent(section, element);
  }
};

/**
 * Checks if any of the links in the given links group is active based on current URL's pathname.
 * @param links - The array of links to check.
 * @param pathName - The current URL's pathname.
 * @returns A boolean indicating whether any of the links in the group is active.
 */
export const isActiveLinkGroup = (links: HeaderLinksGroup["headerLinksGroupItems"], pathName: string) =>
  links.some(({ headerLink }) => {
    const linkSource = getLinkSource(headerLink);

    const { activeLinkPath } = getActiveLink(linkSource, pathName);

    return linkSource === activeLinkPath;
  });

const commonStyles = {
  _visited: {},
  fontSize: "inherit",
  color: "inherit",
  fontWeight: "normal",
  _hover: { fontWeight: { base: "inherit", lg: "medium" }, color: { base: "inherit", lg: "primary.500" } },
};

const hoverCommonStyles = {
  _after: {
    content: "attr(data-text)",
    height: 0,
    overflow: "hidden",
    visibility: "hidden",
    fontWeight: "bold",
    display: "block",
    userSelect: "none",
    pointerEvents: "none",
  },
};

const hoverLinksGroupStyles = {
  _after: { ...hoverCommonStyles._after, pr: 7 },
};

export const groupLinksStyles = {
  _focusVisible: {},
  ...commonStyles,
  ...hoverLinksGroupStyles,
};

export const hoverLinkStyles = {
  ...hoverCommonStyles,
  ...hoverCommonStyles,
};

export const singleLinkStyles = {
  ...hoverLinkStyles,
  ...commonStyles,
};

export const activeGroupLinkStyles = { fontWeight: "medium" };
export const activeSingleLinkStyles = { textDecoration: "underline", fontWeight: "medium" };

/**
 * Returns the styles for a link based on its active state and type.
 * @param isActive - The link active state.
 * @param linkType - The type of link, either "singleLink" or "groupLink".
 * @returns The styles for the link.
 */
export const setLinkStyles = (isActive: boolean, linkType: "singleLink" | "groupLink") =>
  ({
    singleLink: isActive ? { ...singleLinkStyles, ...activeSingleLinkStyles } : singleLinkStyles,
    groupLink: isActive ? { ...groupLinksStyles, ...activeGroupLinkStyles } : groupLinksStyles,
  }[linkType]);

/**
 * Builds a test ID for a menu item based on its name.
 * @param name - The name of the menu item.
 * @returns The test ID for the menu item.
 */
export const buildTestId = (name: string) => `menu_${name.split(" ").join("_").toLocaleLowerCase()}`;

/**
 * Sets the styles for the subheader based on the scroll direction.
 * @param scrollDirection The direction of the scroll. Can be "up" or "down".
 * @returns The styles for the subheader.
 */
export const setSubHeaderStyles = (scrollDirection: ScrollDirection) =>
  ({
    up: { maxHeight: "100px", transition: "max-height 0.3s ease-in, opacity 0.3s ease-in", opacity: 1 },
    down: {
      maxHeight: "0px",
      transition: "max-height 0.3s ease-out, opacity 0.3s ease-out",
      overflow: "hidden",
      opacity: 0,
    },
  }[scrollDirection]);
